import React from "react";
import { inject, observer } from "mobx-react";
import Store from "../../stores/NewExistingOwnerStore";
import { FormData, FormFieldState, UserStatus } from "../../models/NewExistingOwner";
import OwnerNav from "./OwnerNav";
import { Disclaimer, DisclaimerTypes, FormField, FieldTypes } from "./";

interface IProps {
  store?: Store;
}

interface IState extends FormData {
  invalidFields: string[];
  trigger: number; //To trigger fields validation
}

@inject("store")
@observer
class Details extends React.Component<IProps, IState> {
  state: IState = {
    invalidFields: [],
    commsPhone: false,
    commsEmail: false,
    commsSms: false,
    commsPost: false,
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    dob: "",
    streetAddress: "",
    suburb: "",
    postcode: "",
    state: "",
    purchaseDate: "",
    roadsideAssist: false,
    optSurvey: false,
    optPrivacy: false,
    optMotoringClub: false,
    trigger: 0
  };

  private fieldRefs: any = {
    suburb: {},
    state: {}
  };

  get store(): Store {
    return this.props.store;
  }

  constructor(props: IProps) {
    super(props);
    if (props.store.useSampleFormData) {
      this.state = {
        trigger: 0,
        invalidFields: [],
        ...props.store.sampleFormData
      };
    }
  }

  private handleSubmit(): void {
    const trigger: number = this.state.trigger + 1;

    this.setState({ trigger }, () => {
      if (this.state.invalidFields.length < 1 || this.props.store.enableInvalidFormSubmit) {
        this.store.submitFormData(this.state);
      }
    });
  }

  private handleFieldUpdate(stateKey: string, state: FormFieldState): void {
    const newState = {};
    newState[stateKey] = state.value;
    this.setState(newState, () => this.updateInvalidFields(stateKey, state.isValid));
  }

  private updateInvalidFields(stateKey: string | string[], isValid): void {
    let fields = this.state.invalidFields;
    const updates: string[] = Array.isArray(stateKey) ? stateKey : [stateKey];

    updates.forEach(field => {
      if (isValid) {
        fields = fields.filter(item => field !== item);
      } else {
        const index: number = fields.indexOf(field);
        if (index < 0) {
          fields.push(field);
        }
      }
    });

    this.setState({ invalidFields: fields });
  }

  private showDobModal(e?: any) {
    const content = {
      text: `<div>
          <p>Subaru's Roadside Assistance and Club Membership offer is standard upon activation with the relevant Motoring Club in each state. To be eligible, you must supply a valid phone number, owner name, street address and date of birth. By agreeing to this offer, you are deemed to have accepted the terms and conditions, and agree for Subaru Australia to provide your Personal Information (as set out in this New Owner Form) to the relevant Motoring Club so they can use, store and disclose it for the purpose of providing you with membership services and for any other related purpose. For full Terms and Conditions, please go to https://www.subaru.com.au/owner-experience/benefits/program/roadside. If you wish to understand how your local state Motoring Club deals with the Privacy of your Personal Information, or if you wish to access any information that your state Motoring Club holds about you, please contact your local state Motoring Club directly.</p>
        </div>`
    };

    if (e) {
      e.preventDefault();
    }

    const evt = new CustomEvent("showPopupModal", { detail: { content } });
    window.dispatchEvent(evt);
  }

  render() {
    const {
      commsPhone,
      commsEmail,
      commsSms,
      commsPost,
      firstName,
      lastName,
      phone,
      email,
      dob,
      streetAddress,
      suburb,
      postcode,
      state,
      purchaseDate,
      roadsideAssist,
      optSurvey,
      optPrivacy,
      optMotoringClub,
      trigger
    } = this.state;
    const { userStatus, isLoading, warrantyEligible } = this.store;
    const enableSubmit: boolean = !isLoading;
    const isWarrantyEligible: boolean = userStatus === UserStatus.New && warrantyEligible;

    return (
      <section>
        <OwnerNav active={1} />

        <div className="pageSection__content container">
          <div className="text-center">
            <h1 className="h1">
              {this.store.userStatus === UserStatus.New ? "register your Subaru" : "update your details"}
            </h1>
            <h2 className="h2">2. My details</h2>
          </div>

          <div className="row">
            <div className="col-xs-12 col-md-8 col-lg-6 col-md-offset-2 col-lg-offset-3">
              <p>How would you like Subaru Australia to communicate with you?</p>

              <form
                ref={el => (this.fieldRefs.form = el)}
                onSubmit={e => {
                  e.preventDefault();
                  enableSubmit && this.handleSubmit();
                }}
              >
                <div className="row">
                  <div className="checkboxButton__group">
                    <FormField
                      type={FieldTypes.CheckboxBtn}
                      trigger={trigger}
                      defaultValue={commsPhone}
                      label="Phone"
                      optional={true}
                      onChange={(res: FormFieldState) => this.handleFieldUpdate("commsPhone", res)}
                    />
                    <FormField
                      type={FieldTypes.CheckboxBtn}
                      trigger={trigger}
                      defaultValue={commsEmail}
                      label="Email"
                      optional={true}
                      onChange={(res: FormFieldState) => {
                        this.handleFieldUpdate("commsEmail", res);
                        !res.value && this.updateInvalidFields("email", true);
                      }}
                    />
                    <FormField
                      type={FieldTypes.CheckboxBtn}
                      trigger={trigger}
                      defaultValue={commsSms}
                      label="SMS"
                      optional={true}
                      onChange={(res: FormFieldState) => this.handleFieldUpdate("commsSms", res)}
                    />
                    <FormField
                      type={FieldTypes.CheckboxBtn}
                      trigger={trigger}
                      defaultValue={commsPost}
                      label="Post"
                      optional={true}
                      onChange={(res: FormFieldState) => this.handleFieldUpdate("commsPost", res)}
                    />
                  </div>
                </div>

                <div className="row">
                  <FormField
                    type={FieldTypes.Text}
                    trigger={trigger}
                    defaultValue={firstName}
                    label="First name"
                    optional={false}
                    onChange={(res: FormFieldState) => this.handleFieldUpdate("firstName", res)}
                  />
                  <FormField
                    type={FieldTypes.Text}
                    trigger={trigger}
                    defaultValue={lastName}
                    label="Last name"
                    optional={false}
                    onChange={(res: FormFieldState) => this.handleFieldUpdate("lastName", res)}
                  />
                </div>

                <div className="row">
                  <FormField
                    type={FieldTypes.Phone}
                    trigger={trigger}
                    defaultValue={phone}
                    label="Phone"
                    optional={false}
                    onChange={(res: FormFieldState) => this.handleFieldUpdate("phone", res)}
                  />
                  <FormField
                    type={FieldTypes.Email}
                    trigger={trigger}
                    defaultValue={email}
                    label="Email"
                    optional={false}
                    errorMsg={"Enter a valid email"}
                    onChange={(res: FormFieldState) => this.handleFieldUpdate("email", res)}
                  />
                </div>

                <div className="row">
                  <FormField
                    type={FieldTypes.Postcode}
                    trigger={trigger}
                    defaultValue={postcode}
                    label="Postcode"
                    optional={false}
                    errorMsg={"Select your location"}
                    onChange={(res: FormFieldState) => {
                      const { postcode, name, state } = res.value;
                      this.fieldRefs.suburb.setValue(name || "");
                      this.fieldRefs.state.setValue(state || "");

                      this.setState({ postcode: postcode || "", state, suburb: name }, () => {
                        this.updateInvalidFields(["postcode", "state", "suburb"], res.isValid);
                      });
                    }}
                  />
                  <FormField
                    type={FieldTypes.Text}
                    trigger={trigger}
                    defaultValue={streetAddress}
                    label="Street Address"
                    optional={false}
                    onChange={(res: FormFieldState) => this.handleFieldUpdate("streetAddress", res)}
                  />
                </div>

                <div className="row">
                  <FormField
                    ref={el => (this.fieldRefs.suburb = el)}
                    type={FieldTypes.Static}
                    trigger={trigger}
                    defaultValue={suburb}
                    label="Suburb"
                    optional={false}
                    placeholder={"Enter postcode to select"}
                    onChange={(res: FormFieldState) => {
                      this.setState({ suburb: res.value }, () => this.updateInvalidFields("suburb", res.isValid));
                    }}
                  />
                  <FormField
                    ref={el => (this.fieldRefs.state = el)}
                    type={FieldTypes.Static}
                    trigger={trigger}
                    defaultValue={state}
                    label="State"
                    optional={false}
                    placeholder={"Enter postcode to select"}
                    onChange={(res: FormFieldState) => {
                      this.setState({ state: res.value }, () => this.updateInvalidFields("state", res.isValid));
                    }}
                  />
                </div>

                <div className="row">
                  {isWarrantyEligible && (
                    <FormField
                      type={FieldTypes.Dob}
                      trigger={trigger}
                      defaultValue={dob}
                      label="Date of Birth"
                      labelDisclaimer={() => this.showDobModal()}
                      optional={!roadsideAssist}
                      minDate={"19000101"}
                      maxDate={new Date()}
                      onChange={(res: FormFieldState) => {
                        this.setState({ dob: res.value }, () => {
                          this.updateInvalidFields("dob", res.isValid);
                        });
                      }}
                    />
                  )}

                  <FormField
                    type={FieldTypes.Datepicker}
                    trigger={trigger}
                    defaultValue={purchaseDate}
                    label="Approximate vehicle puchase date"
                    optional={false}
                    minDate={"19500101"}
                    maxDate={new Date()}
                    onChange={(res: FormFieldState) => {
                      this.setState({ purchaseDate: res.value }, () => {
                        this.updateInvalidFields("purchaseDate", res.isValid);
                      });
                    }}
                  />
                </div>

                {isWarrantyEligible && (
                  <div className="row">
                    <div className="col-xs-12">
                      <div className="neo__5yr">
                        <div
                          className={`neo__5yrCheck ${roadsideAssist ? "active" : ""}`}
                          onClick={() => {
                            const roadsideAssist = !this.state.roadsideAssist;
                            this.setState({ roadsideAssist }, () => {
                              if (!roadsideAssist) {
                                this.updateInvalidFields("dob", true);
                                this.setState({ optMotoringClub: false });
                              }
                            });
                          }}
                        >
                          <span className="neo__5yrTick" /> I'd like to activate the balance of my 5 year Roadside
                          Assistance and Club Membership
                          <span className="tt">
                            *
                            <span className="tt__content">
                              This vehicle includes 12 months or more of Subaru's complimentary roadside assitance and
                              Club Membership with your local State Motoring Club. For further information, call us on
                              1800 22 66 43.
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row">
                  {isWarrantyEligible && roadsideAssist ? (
                    <FormField
                      className="col-xs-12"
                      type={FieldTypes.Checkbox}
                      trigger={trigger}
                      defaultValue={optMotoringClub}
                      label="I would like to be contacted by the Motoring Club for marketing purposes including exclusive offers and benefits."
                      optional={true}
                      onChange={(res: FormFieldState) => this.handleFieldUpdate("optMotoringClub", res)}
                    />
                  ) : null}
                  <FormField
                    className="col-xs-12"
                    type={FieldTypes.Checkbox}
                    trigger={trigger}
                    defaultValue={optSurvey}
                    label="I would like to participate in customer research surveys"
                    optional={true}
                    onChange={(res: FormFieldState) => this.handleFieldUpdate("optSurvey", res)}
                  />
                  <FormField
                    className="col-xs-12"
                    type={FieldTypes.Checkbox}
                    trigger={trigger}
                    defaultValue={optPrivacy}
                    label={`Please confirm you have read and agreed to our <b>Privacy Collection Statement</b> below by checking this box.`}
                    optional={false}
                    errorMsg="You must agree to the Privacy Collection Statement"
                    onChange={(res: FormFieldState) => this.handleFieldUpdate("optPrivacy", res)}
                  />
                </div>

                <div className="row text-center">
                  <button type="submit" className="btn btn--primary" disabled={!enableSubmit}>
                    {userStatus === UserStatus.New ? (
                      <span>Complete registration</span>
                    ) : (
                      <span>Complete my details</span>
                    )}
                  </button>

                  <p className="neo__completeTerms">
                    By clicking "
                    {userStatus === UserStatus.New ? (
                      <span>Complete registration</span>
                    ) : (
                      <span>Complete my details</span>
                    )}
                    " you are confirming that you are the legal owner of this vehicle.
                  </p>
                </div>
              </form>
            </div>
          </div>

          <Disclaimer type={DisclaimerTypes.Details} />
        </div>
      </section>
    );
  }
}

export default Details;
